(() => {
  const engines = document.querySelectorAll('[data-twelve-human-engine]');
  if (!engines.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const range = (start, end, value) => clamp((value - start) / (end - start));
  const smooth = (start, end, value) => {
    const amount = range(start, end, value);
    return amount * amount * (3 - 2 * amount);
  };
  const seeded = (number) => {
    const value = Math.sin(number * 999.17) * 43758.5453;
    return value - Math.floor(value);
  };

  engines.forEach((engine) => {
    if (engine.dataset.engineInitialized === 'true') return;
    engine.dataset.engineInitialized = 'true';

    const canvas = engine.querySelector('[data-engine-canvas]');
    const context = canvas?.getContext('2d');
    const hotspots = engine.querySelectorAll('.twelve-engine__hotspots span');
    const skip = engine.querySelector('[data-engine-skip]');
    const dots = [];
    let progress = 0;
    let queued = false;
    let visible = false;

    const buildDots = () => {
      if (!canvas || !context) return;
      const bounds = engine.querySelector('.twelve-engine__sticky').getBoundingClientRect();
      const density = bounds.width < 500 ? 420 : 760;
      dots.length = 0;

      for (let index = 0; index < density; index += 1) {
        const shortsBand = index >= density * 0.63;
        dots.push({
          x: bounds.width * (0.24 + seeded(index) * 0.52),
          y: bounds.height * (shortsBand ? 0.55 + seeded(index + 31) * 0.27 : 0.24 + seeded(index + 17) * 0.32),
          angle: seeded(index + 9) * Math.PI * 2,
          size: 0.45 + seeded(index + 22) * 1.35,
        });
      }
    };

    const resizeCanvas = () => {
      if (!canvas || !context) return;
      const sticky = engine.querySelector('.twelve-engine__sticky');
      const bounds = sticky.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(bounds.width * ratio);
      canvas.height = Math.round(bounds.height * ratio);
      canvas.style.width = `${bounds.width}px`;
      canvas.style.height = `${bounds.height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      buildDots();
    };

    const drawParticles = () => {
      if (!canvas || !context) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const explosion = smooth(0.26, 0.64, progress) * (1 - smooth(0.70, 0.84, progress));
      const opacity = Math.sin(clamp(explosion) * Math.PI) * 0.78;
      const color = getComputedStyle(engine).getPropertyValue('--engine-cyan').trim() || '#83dcff';

      context.clearRect(0, 0, width, height);
      context.fillStyle = color;

      dots.forEach((dot, index) => {
        const distance = (24 + seeded(index + 50) * 130) * explosion;
        const x = dot.x + Math.cos(dot.angle) * distance;
        const y = dot.y + Math.sin(dot.angle) * distance;
        context.globalAlpha = opacity * (0.25 + seeded(index + 4) * 0.75);
        context.fillRect(x, y, dot.size, dot.size);
      });

      context.globalAlpha = 1;
    };

    const setProperties = () => {
      if (reduceMotion.matches) {
        engine.querySelector('[data-engine-final]').inert = false;
        progress = 1;
        engine.style.setProperty('--engine-progress', 1);
        engine.style.setProperty('--engine-open', 1);
        engine.style.setProperty('--engine-product', 1);
        engine.style.setProperty('--engine-scan-opacity', 0);
        engine.style.setProperty('--engine-reveal', 0);
        engine.style.setProperty('--engine-final', 1);
        engine.style.setProperty('--engine-turn', 0);
        engine.style.setProperty('--engine-cue', 0);
        hotspots.forEach((hotspot) => hotspot.style.setProperty('--engine-visible', 0));
        drawParticles();
        return;
      }

      const rect = engine.getBoundingClientRect();
      const distance = Math.max(1, rect.height - window.innerHeight);
      progress = clamp(-rect.top / distance);

      const opening = smooth(0.08, 0.22, progress);
      const product = (1 - smooth(0.70, 0.82, progress)) + smooth(0.84, 0.96, progress);
      const reveal = smooth(0.66, 0.78, progress) * (1 - smooth(0.82, 0.90, progress));
      const final = smooth(0.86, 0.97, progress);
      const finalPanel = engine.querySelector('[data-engine-final]');
      finalPanel.inert = final < 0.5;

      engine.style.setProperty('--engine-progress', progress.toFixed(4));
      engine.style.setProperty('--engine-open', opening.toFixed(3));
      engine.style.setProperty('--engine-product', product.toFixed(3));
      engine.style.setProperty('--engine-scan-opacity', (smooth(0.12, 0.20, progress) * (1 - smooth(0.55, 0.66, progress))).toFixed(3));
      engine.style.setProperty('--engine-scan-y', range(0.12, 0.62, progress).toFixed(3));
      engine.style.setProperty('--engine-reveal', reveal.toFixed(3));
      engine.style.setProperty('--engine-final', final.toFixed(3));
      engine.style.setProperty('--engine-turn', (Math.sin(range(0.1, 0.72, progress) * Math.PI * 2) * 11).toFixed(2));
      engine.style.setProperty('--engine-cue', (1 - smooth(0.03, 0.11, progress)).toFixed(3));

      hotspots.forEach((hotspot, index) => {
        const start = 0.20 + index * 0.078;
        const hotspotOpacity = smooth(start, start + 0.012, progress) * (1 - smooth(start + 0.06, start + 0.075, progress));
        hotspot.style.setProperty('--engine-visible', hotspotOpacity.toFixed(3));
      });

      drawParticles();
    };

    const render = () => {
      queued = false;
      if (visible || reduceMotion.matches) setProperties();
    };

    const requestRender = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(render);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visible = entry.isIntersecting;
        if (visible) requestRender();
      });
    }, { rootMargin: '75% 0px 75% 0px' });

    skip?.addEventListener('click', (event) => {
      const target = document.getElementById('TwelveYouVsYou');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      target.focus({ preventScroll: true });
    });

    observer.observe(engine);
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', () => {
      resizeCanvas();
      requestRender();
    }, { passive: true });
    reduceMotion.addEventListener?.('change', requestRender);
    resizeCanvas();
    setProperties();
  });
})();
